import { TeamSettings } from '@fullstory/proto/fs/api/clientapi';
import {
  ApiKeyMetadata,
  GetSeatsAndInvitesResponse,
  PrivacySettingsResponse,
  Role,
} from '@fullstory/proto/fs/services/orgs';
import { createSelector } from 'reselect';
import { AppState } from '../../types';

export const rootSelector = (state: AppState) => state.settings;

export const getSettingsResponse = (state: AppState) => state.settings.settings.data;

export const getOrgHistory = (state: AppState): number =>
  state.settings.settings.data?.plan?.history || 0;

export const getOrgAnalyticsHistory = (state: AppState): number =>
  state.settings.settings.data?.plan?.analyticsHistory || 0;

export const getSeatSettings = (state: AppState) => state.settings.seatSettings;

export const getApiKeys = (state: AppState): ApiKeyMetadata[] => state.settings.apiKeys;

export const getPrivacySettings = (state: AppState): PrivacySettingsResponse | null =>
  state.settings.privacySettings;

export const getSeatsInvitesInitialized = (state: AppState) =>
  state.settings.seatsInvitesInitialized;

export const getSeatsInvites = (state: AppState): GetSeatsAndInvitesResponse =>
  state.settings.seatsInvites;

export const getGuests = (state: AppState): GetSeatsAndInvitesResponse => state.settings.guests;

export const getTeamSettings = (state: AppState): TeamSettings =>
  state.settings.teamSettings ?? {
    seatsAndInvites: null,
    totalSeats: 0,
    /** google, auth0, ... */
    idProvider: '',
    idProviderEnforced: false,
    auth0TeamName: '',
    userIsGoogleAuthed: false,
    shareEmailDomains: [],
    joinOrgEnabled: true,
    joinOrgDomains: [],
  };

/**
 * Seat roles eligible to be specified as a contact on an org or umbrella.
 */
const ORG_CONTACTS_INCLUDED_ROLES: { [key in Role]: boolean } = {
  [Role.UNKNOWN]: false,
  [Role.ADMIN]: true,
  [Role.USER]: true,
  [Role.VIEW_ONLY]: false,
  [Role.UMBRELLA_MANAGER]: true,
  [Role.ARCHITECT]: true,
  [Role.EXPLORER]: false,
};

/**
 * Emails of seats eligible to be specified as a contact on an org.
 */
export const getOrgEligibleContactEmails = createSelector(
  getTeamSettings,
  teamSettings =>
    teamSettings?.seatsAndInvites?.seats.reduce<string[]>((acc, seat) => {
      if (seat.id && ORG_CONTACTS_INCLUDED_ROLES[seat.role]) {
        acc.push(seat.id.email);
      }
      return acc;
    }, []) || [],
);

export const getCustomSeatRequestSettings = (state: AppState) =>
  state.settings.customSeatRequestSettings;

export const getExperimentConfig = createSelector(
  rootSelector,
  settings => settings.experimentConfig,
);
